export const AUTHENTICATOR_GUIDE = {
  id: "authenticator-guide",
  title: "Authenticator (TOTP)",
  description:
    "Store and generate two-factor authentication codes for your services directly in the platform. Share codes with team members and scan QR codes to add accounts.",
  icon: "KeyRound",
  version: "1.0.0",
  lastUpdated: "2026-03-19",
  roles: ["admin", "exec", "sales"],
  sections: [
    {
      id: "what-is-it",
      title: "What Is the Authenticator?",
      content: [
        {
          type: "paragraph",
          text: "The **Authenticator** tab is a built-in TOTP (Time-based One-Time Password) generator — it replaces standalone apps like Google Authenticator, Microsoft Authenticator, or Authy.",
        },
        {
          type: "paragraph",
          text: "When a service (GitHub, AWS, Azure, etc.) asks you to set up two-factor authentication (2FA), you normally scan a QR code with your phone. Instead, you can add it here. The key differences:",
        },
        {
          type: "table",
          headers: ["", "Phone Authenticator", "Platform Authenticator"],
          rows: [
            ["Secrets stored", "On your phone", "In the cloud (Cosmos DB)"],
            ["Access from", "That device only", "Any browser, any device"],
            ["Lost phone?", "Locked out (unless backup)", "Still works — just log in"],
            ["Team sharing", "Not possible", "Share by email, built-in"],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "Your TOTP secrets are stored per-user and per-tenant. FGF and L30 users have completely separate vaults.",
        },
      ],
    },

    {
      id: "adding-accounts",
      title: "Adding an Account",
      content: [
        {
          type: "paragraph",
          text: "Click **Add Account** in the top-right. There are three ways to add a TOTP account:",
        },
        {
          type: "heading",
          level: 3,
          text: "1. Scan QR Code (recommended)",
        },
        {
          type: "paragraph",
          text: "The **Scan QR** tab is selected by default. You have two options:",
        },
        {
          type: "steps",
          items: [
            "**Upload image** — Screenshot the QR code from the service's 2FA setup page, then click the upload area to select it. The QR code is decoded and the account is added automatically.",
            "**Use camera** — On devices with a camera, the live viewfinder appears below. Point it at the QR code on your screen or a printed page. The code is scanned automatically.",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "On a desktop, the easiest flow is: screenshot the QR code from the service, then upload it. On a phone, point the camera at the QR on another screen.",
        },
        {
          type: "heading",
          level: 3,
          text: "2. Manual Entry",
        },
        {
          type: "paragraph",
          text: "If the service shows a text secret key (usually labelled \"Can't scan? Enter manually\"), switch to the **Manual** tab:",
        },
        {
          type: "steps",
          items: [
            "Enter the **Issuer / Service** name (e.g. GitHub, AWS)",
            "Optionally enter an **Account Name** (e.g. your email address on that service)",
            "Paste the **Secret Key** — a Base32 string like `JBSWY3DPEHPK3PXP`",
            "Click **Add Account**",
          ],
        },
        {
          type: "heading",
          level: 3,
          text: "3. Paste URI",
        },
        {
          type: "paragraph",
          text: "Some services let you copy the raw `otpauth://` URI. Switch to the **Paste URI** tab and paste it. This is the same data that QR codes encode, just in text form.",
        },
        {
          type: "code",
          text: "otpauth://totp/GitHub:user@example.com?secret=JBSWY3DPEHPK3PXP&issuer=GitHub",
        },
      ],
    },

    {
      id: "using-codes",
      title: "Using Codes",
      content: [
        {
          type: "paragraph",
          text: "Once accounts are added, the Authenticator page shows a live 6-digit code for each account. Codes refresh every 30 seconds.",
        },
        {
          type: "heading",
          level: 3,
          text: "The Timer Bar",
        },
        {
          type: "paragraph",
          text: "A colour-coded progress bar at the top shows how much time is left before codes rotate:",
        },
        {
          type: "table",
          headers: ["Colour", "Time Remaining"],
          rows: [
            ["Accent (blue/gold)", "More than 10 seconds"],
            ["Amber", "5–10 seconds"],
            ["Red", "Less than 5 seconds"],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "If the bar is red, wait a moment for the next code — entering an expiring code often fails on the target service.",
        },
        {
          type: "heading",
          level: 3,
          text: "Copying a Code",
        },
        {
          type: "paragraph",
          text: "Two ways to copy:",
        },
        {
          type: "steps",
          items: [
            "**Click the code itself** — the large 6-digit number is clickable",
            "**Click the copy icon** — the clipboard button on the right",
          ],
        },
        {
          type: "paragraph",
          text: "A green tick confirms the code was copied to your clipboard.",
        },
        {
          type: "heading",
          level: 3,
          text: "Viewing the Secret",
        },
        {
          type: "paragraph",
          text: "Click the **eye icon** to reveal the underlying Base32 secret key. This is useful if you need to transfer the account to another device or authenticator app.",
        },
      ],
    },

    {
      id: "sharing",
      title: "Sharing with Team Members",
      content: [
        {
          type: "paragraph",
          text: "You can share any TOTP account you own with other users in your tenant. This is useful for shared service accounts (e.g. a team AWS root account, a shared social media login).",
        },
        {
          type: "heading",
          level: 3,
          text: "How to Share",
        },
        {
          type: "steps",
          items: [
            "Click the **share icon** on the account card (only visible on accounts you own)",
            "In the modal, type a team member's **email address** and press Enter or click Add",
            "Add as many people as needed",
            "Click **Save** to apply",
          ],
        },
        {
          type: "heading",
          level: 3,
          text: "How It Looks",
        },
        {
          type: "paragraph",
          text: "**For the owner:** A purple badge with a user count appears on shared entries. You can update or revoke sharing at any time by opening the share modal and removing emails.",
        },
        {
          type: "paragraph",
          text: "**For shared users:** The entry appears in a separate \"Shared with me\" section with a green \"shared\" badge. The owner's email is shown. Shared users can view and copy codes, but cannot delete the entry or share it with others.",
        },
        {
          type: "callout",
          variant: "warning",
          text: "Only share TOTP accounts when necessary. Anyone you share with can generate valid codes for that service. Revoke access when it's no longer needed.",
        },
      ],
    },

    {
      id: "deleting",
      title: "Removing Accounts",
      content: [
        {
          type: "paragraph",
          text: "Click the **trash icon** on any account you own. You'll be asked to confirm before it's deleted. Deleting an entry also removes it from anyone it was shared with.",
        },
        {
          type: "callout",
          variant: "warning",
          text: "Before deleting, make sure you've disabled 2FA on the target service or transferred the secret to another authenticator. Otherwise you could lose access to that service.",
        },
      ],
    },

    {
      id: "security",
      title: "Security Considerations",
      content: [
        {
          type: "steps",
          items: [
            "**Secrets are stored server-side** in Cosmos DB, encrypted at rest by Azure. They are fetched over HTTPS and used in-browser to generate codes — the TOTP algorithm runs entirely client-side.",
            "**Access is per-user** — only you (and anyone you explicitly share with) can see your entries. There is no admin override.",
            "**Tenant isolation** — FGF and L30 have completely separate databases. Sharing only works within the same tenant.",
            "**Session required** — you must be authenticated via Clerk to access your vault. Logging out means codes are no longer accessible until you sign back in.",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "This authenticator is as secure as your platform login. Use a strong authentication method (Microsoft SSO recommended) to protect your TOTP secrets.",
        },
      ],
    },

    {
      id: "faq",
      title: "FAQ",
      content: [
        {
          type: "faq",
          items: [
            {
              question: "Can I use this instead of Google Authenticator?",
              answer:
                "Yes — it generates the same standard TOTP codes (RFC 6238). Any service that supports Google Authenticator, Authy, or any TOTP app will work with this.",
            },
            {
              question: "What happens if I lose my phone?",
              answer:
                "Nothing — your secrets are stored in the cloud, not on your phone. Just log into the platform from any device and your codes are there.",
            },
            {
              question: "Can an admin see my TOTP secrets?",
              answer:
                "No. Entries are scoped to your email address. There is no admin view of other users' authenticator entries.",
            },
            {
              question: "What if the QR scanner doesn't work?",
              answer:
                "Take a screenshot of the QR code and use the image upload option. Or switch to Manual Entry and type the secret key — services always show a text fallback alongside the QR code.",
            },
            {
              question: "My codes don't match — what's wrong?",
              answer:
                "TOTP codes are time-based. If your device clock is significantly out of sync, codes won't match. Make sure your device time is set to automatic/network time.",
            },
            {
              question: "Can I export my accounts to another authenticator?",
              answer:
                "Click the eye icon on any entry to reveal the secret key. You can enter that key into any other TOTP app to transfer the account.",
            },
          ],
        },
      ],
    },
  ],
};
